import ResizableScene from "../core/ResizableScene";
import Button from "../core/Button";
import ConfirmResetPopup from "../ui/ConfirmResetPopup";
import PlayerData from "../PlayerData";
import { StoryScreen } from "../ui/StoryScreen";
import { PlayerDataUtils } from "../utils/PlayerDataUtils";
import { TapHand } from "../game/TapHand";
import ScreenTransition from "./ScreenTransition";
import LoadingContainer, { LoadingContainerState } from "../ui/LoadingContainer";

export default class SplashScene extends ResizableScene {
    private resetButton: Button;
    private tutHand: TapHand;
    playButton: Button;
    shopButton: Button;
    loadingContainer: LoadingContainer;
    loadingContainerState: LoadingContainerState;

    init(containerState: LoadingContainerState): void {
        this.loadingContainerState = containerState;
        super.init();
    }

    create(): void {
        super.create();
        this.music.play("music");

        const loadingContainer = this.loadingContainer = new LoadingContainer(this, false, false, this.loadingContainerState);
        this.add.existing(loadingContainer);

        const playButton = this.playButton = this.add.button(0, 0, "splashPlayButton", undefined, () => {
            const spineContainer = this.add.spineContainer(playButton.x, playButton.y);
            this.children.moveBelow(spineContainer, playButton);
            const spine = this.add.spine(0, 0, "explosion-generic");
            spineContainer.add(spine);
            spine.play("hit", false);
            Button.buttonInputEnabled = false;
            playButton.disableInteractive();
            spine.on("complete", () => {
                Button.buttonInputEnabled = true;
                this.play();
            });
        }, undefined, true);

        const resetButton = (this.resetButton = this.add.button(0, 0, "resetButton", 0, this.reset, this));
        if (PlayerData.Instance.saveData.firstTimeUser) {
			resetButton.setVisible(false);
        }
        
        const shopButton = this.shopButton = this.add.button(0, 0, "shopButton", 0, this.shop, this);
        shopButton.setSize(shopButton.displayWidth, shopButton.displayHeight);

        if(PlayerDataUtils.CanUnlockFirstCharacter() || PlayerDataUtils.PlayerCanBuyFirstItem()) {
            this.tutHand = new TapHand(this);
            this.tutHand.setVisible(false);
            this.add.existing(this.tutHand);
        }

        const soundOn = !this.soundManager.muted;
		const soundButtonKey = soundOn ? "soundButtonOn" : "soundButtonOff";
        const soundButton = this.add.button(0, 0, soundButtonKey, undefined, () => this.toggleSound(soundButton), undefined, true);

        this.addResize((): void => {
            const scale = Math.min(this.viewport.width / 1920, 1);
            playButton.setScale(1 - (1 - scale));
            this.alignIn.BottomRight(playButton, this.viewport, -60 + (playButton.width-playButton.displayWidth)*0.6, -20 + (playButton.height-playButton.displayHeight)*0.6);
            shopButton.setScale(1 - (1 - scale));
            this.alignTo.LeftBottom(shopButton, playButton, 40 - (shopButton.width-shopButton.displayWidth), -10);
            this.alignIn.BottomLeft(resetButton, this.viewport, -20, -20);
            this.alignIn.TopRight(soundButton, this.viewport, -34, -34);
            if(this.tutHand) this.alignIn.Center(this.tutHand, shopButton);
        });

        this.resize();

        if (PlayerData.Instance.saveData.firstTimeUser) shopButton.setVisible(false);
        const addIntroTween = (item: Button, delay: number) => {
            this.tweens.add({
                targets: item,
                scale: item.scale,
                ease: Phaser.Math.Easing.Back.Out,
                duration: 300,
                delay: delay,
                onComplete: () => {
                    if (this.tutHand) this.tutHand.setVisible(true);
                }
            });
            item.scale = 0;
        };

        addIntroTween(playButton, 100);
        addIntroTween(soundButton, 0);
        addIntroTween(shopButton, 50);
        addIntroTween(resetButton, 150);

        // const addIntroTween = (item: Button, dx: number, dy: number) => {
        //     this.tweens.add({
        //         targets: item,
        //         x: { from: item.x + dx, to: item.x },
        //         y: { from: item.y + dy, to: item.y },
        //         ease: Phaser.Math.Easing.Quadratic.Out,
        //         duration: 300,
        //         onComplete: ()=> {
        //             if(this.tutHand) this.tutHand.setVisible(true);
        //         }
        //     });
        //     item.x += dx;
        //     item.y += dy;
        // };

        // addIntroTween(playButton, 0, 400);
        // addIntroTween(soundButton, 400, 0);
        // addIntroTween(shopButton, 0, 400);
        // addIntroTween(resetButton, 0, 400);

        this.updateMissions();
    }

    play(): void {
        if (PlayerData.Instance.saveData.firstTimeUser) {
            //PlayerData.Instance.saveData.firstTimeUser = false;
            PlayerData.Instance.save();
            this.playButton.disableInteractive();
            this.shopButton.disableInteractive();
            ScreenTransition.enter(this, () => {
                const storyScreen = new StoryScreen(this);
                this.add.existing(storyScreen);
                ScreenTransition.exit(this);
            });
        }
        else {
            this.scene.start("PreloaderScene", { destinationScene: "GameScene", loadingContainerState: this.loadingContainer.containerState });
        }
    }

    updateMissions(): void{
		const saveData = PlayerData.Instance.saveData;
		// progress missions
        if (saveData) {
            PlayerData.progressMissions(saveData);
        }
	}

    reset(): void {
        if(ConfirmResetPopup.isOpen) return;
        this.resetButton.disableInteractive();
		const popup = new ConfirmResetPopup(this, () => {
            this.resetButton.setVisible(false);
            this.shopButton.setVisible(false);
            if(this.tutHand) this.tutHand.setVisible(false);
        },
        () => this.resetButton.setInteractive(), this);
		this.add.existing(popup);
    }
    
    shop(): void {
        this.scene.start("PreloaderScene", { destinationScene: "ShopScene", loadingContainerState: this.loadingContainer.containerState });
    }

    toggleSound(btn: Button): void {
		this.soundManager.muted = !this.soundManager.muted;
		this.sound.mute = this.soundManager.muted;
		this.music.muted = !this.music.muted;
		if (!this.soundManager.muted) btn.setTexture("soundButtonOn");
		else btn.setTexture("soundButtonOff");
	}

    shutdown(): void {
        super.shutdown();
        if(this.tutHand) this.tutHand.destroy();
    }
}
